import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  BookOpen, 
  Clock, 
  Star, 
  Users,
  ArrowRight,
  Play
} from 'lucide-react';
import CourseCard from '../../components/Cards/CourseCard';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const Courses = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // Mock courses data
  const courses = [
    {
      id: 1,
      title: 'Advanced Python Programming',
      description: 'Master advanced Python concepts and frameworks',
      duration_hours: 120,
      difficulty_level: 'intermediate',
      average_rating: 4.8,
      enrollment_count: 45,
      skills_taught: ['Python', 'Django', 'Flask', 'API Development'],
      category: 'Programming',
      price: 2999,
      instructor: 'Dr. Sarah Johnson',
      thumbnail: '/api/placeholder/300/200',
      similarity_score: 0.85,
      ai_insight: 'This course perfectly matches your Python skills and web development interests.',
      recommendation_reason: 'Course 1 matches your profile based on AI analysis'
    },
    {
      id: 2,
      title: 'React Development Bootcamp',
      description: 'Complete React development course from basics to advanced',
      duration_hours: 80,
      difficulty_level: 'beginner',
      average_rating: 4.6,
      enrollment_count: 38,
      skills_taught: ['React', 'JavaScript', 'HTML/CSS', 'Redux'],
      category: 'Web Development',
      price: 2499,
      instructor: 'Mike Chen',
      thumbnail: '/api/placeholder/300/200',
      similarity_score: 0.78,
      ai_insight: 'Great starting point for frontend development with React.',
      recommendation_reason: 'Course 2 matches your profile based on AI analysis'
    },
    {
      id: 3,
      title: 'Machine Learning Fundamentals',
      description: 'Introduction to ML concepts and algorithms',
      duration_hours: 100,
      difficulty_level: 'intermediate',
      average_rating: 4.7,
      enrollment_count: 52,
      skills_taught: ['Python', 'Machine Learning', 'Data Science', 'TensorFlow'],
      category: 'Data Science',
      price: 3999,
      instructor: 'Dr. Alex Kumar',
      thumbnail: '/api/placeholder/300/200',
      similarity_score: 0.72,
      ai_insight: 'Excellent course for advancing into AI/ML field.',
      recommendation_reason: 'Course 3 matches your profile based on AI analysis'
    },
    {
      id: 4,
      title: 'Cloud Computing with AWS',
      description: 'Learn cloud computing and AWS services',
      duration_hours: 90,
      difficulty_level: 'intermediate',
      average_rating: 4.5,
      enrollment_count: 28,
      skills_taught: ['AWS', 'Cloud Computing', 'DevOps', 'Docker'],
      category: 'Cloud Computing',
      price: 3499,
      instructor: 'Lisa Wang',
      thumbnail: '/api/placeholder/300/200',
      similarity_score: 0.65,
      ai_insight: 'Valuable skills for modern software development.',
      recommendation_reason: 'Course 4 matches your profile based on AI analysis'
    }
  ];

  const categories = ['all', 'Programming', 'Web Development', 'Data Science', 'Cloud Computing', 'Mobile Development'];
  const levels = ['all', 'beginner', 'intermediate', 'advanced'];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || course.difficulty_level === selectedLevel;
    
    return matchesSearch && matchesCategory && matchesLevel;
  });

  const handleEnroll = (course) => {
    console.log('Enrolling in course:', course.title);
    // Handle enrollment logic
  };

  const handleViewDetails = (course) => {
    console.log('Viewing course details:', course.title);
    // Handle view details logic
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Courses</h1>
          <p className="text-gray-600 mt-2">Discover and enroll in courses that match your goals</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md ${
              viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <Grid className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md ${
              viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <List className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-content">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="lg:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input w-full"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>

            {/* Level Filter */}
            <div className="lg:w-48">
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="input w-full"
              >
                {levels.map(level => (
                  <option key={level} value={level}>
                    {level === 'all' ? 'All Levels' : level.charAt(0).toUpperCase() + level.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Button */}
            <button className="btn btn-outline">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredCourses.length} of {courses.length} courses
        </p>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Sort by:</span>
          <select className="input text-sm">
            <option value="relevance">Relevance</option>
            <option value="rating">Rating</option>
            <option value="price">Price</option>
            <option value="duration">Duration</option>
          </select>
        </div>
      </div>

      {/* Course Grid/List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
            : 'space-y-4'
        }>
          {filteredCourses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <CourseCard
                course={course}
                onEnroll={handleEnroll}
                onViewDetails={handleViewDetails}
              />
            </motion.div>
          ))}
        </div>
      )}

      {/* Load More */}
      {filteredCourses.length > 0 && (
        <div className="text-center">
          <button className="btn btn-outline">
            Load More Courses
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredCourses.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              setSelectedLevel('all');
            }}
            className="btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default Courses;
